/**
 * Phase 0439 — Plan 04 — REALTIME-02 / REALTIME-03
 * ============================================================================
 * Supabase Realtime subscriber on `cogo.application_ontology` per
 * (tenant_id, app_id). On each event:
 *   1. Invokes the caller's `onChange` callback with a normalized
 *      `OntologyChangePayload`.
 *   2. Bridges to the existing `BroadcastChannel('cogo-kg-invalidate')`
 *      listener (Phase 0431 D-C6 in shard-cache.ts L131-L156) so all
 *      same-origin tabs (Studio, Starter, Commerce) invalidate caches.
 *
 * Decisions encoded:
 *   D-D1 — publication scope: cogo.application_ontology (Plan 04 cogo-platform
 *          migration 20260514000003 enables it).
 *   D-D2 — Realtime filter on tenant_id only (Realtime supports a single
 *          =eq filter per channel); app_id filter applied client-side after
 *          delivery.
 *   D-D3 — Bridges to BroadcastChannel('cogo-kg-invalidate') with extended
 *          payload that is backwards-compatible with the existing
 *          shard-cache.ts listener (Phase 0431 D-C6 added 3 fields:
 *          newVersion / oldVersion / lastMigration; we extend with
 *          source / tenantId / appId / branch / changeKind / subject /
 *          receivedAt — older listeners ignore unknown fields).
 *   D-D4 — REALTIME-03 latency budget: callback fires < 1s after INSERT
 *          commit. Validated by `e2e-mcp-realtime.test.ts`.
 *   D-D5 — Auth via SupabaseClient JWT; RLS engaged on Realtime feed.
 *
 * Threat-model:
 *   T-439-04-01 mitigated — RLS on application_ontology scopes the Realtime
 *     feed at the platform level; the tenant_id=eq filter + client-side
 *     app_id filter are defense-in-depth.
 *   T-439-04-03 accepted — onChange callback errors do NOT propagate; the
 *     channel must remain healthy (test #5 covers this).
 *
 * Module is consumed via:
 *   - `import { subscribeOntologyChanges } from '@cogo/kg-browser'` (barrel)
 *   - `import { subscribeOntologyChanges } from '@cogo/kg-browser/realtime-subscriber'`
 *     (tree-shaking-conscious sub-path).
 * ============================================================================
 */

import type { SupabaseClient } from "@supabase/supabase-js"

export interface OntologyChangePayload {
  changeKind: "INSERT" | "UPDATE" | "DELETE"
  subject: string
  tenantId: string
  appId: string
  branch: string
  /** version_seq from the row (Phase 436 STORE-01); undefined if unavailable. */
  versionSeq?: number
  /** Date.now() at the moment the subscriber received the event. */
  receivedAt: number
}

export interface SubscribeOpts {
  tenantId: string
  appId: string
  supabaseClient: SupabaseClient
  onChange: (payload: OntologyChangePayload) => void
  /** Channel name; defaults to `ontology:${tenantId}:${appId}`. */
  channelName?: string
  /**
   * Skip the BroadcastChannel bridge (e.g. for tests that consume `onChange`
   * directly without wanting a tab-wide post). Default: false.
   */
  skipBroadcast?: boolean
}

export type Unsubscribe = () => void

/**
 * Subscribe to `cogo.application_ontology` changes for a single tenant + app.
 * Returns an unsubscribe function that removes the channel cleanly.
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeOntologyChanges({
 *   tenantId: "00000000-0000-0000-0000-000000000000",
 *   appId: "my-app",
 *   supabaseClient: supabase,
 *   onChange: (event) => { console.log("ontology changed:", event); }
 * });
 * // ...
 * unsubscribe();
 * ```
 */
export function subscribeOntologyChanges(opts: SubscribeOpts): Unsubscribe {
  const channelName =
    opts.channelName ?? `ontology:${opts.tenantId}:${opts.appId}`

  const channel = opts.supabaseClient
    .channel(channelName)
    .on(
      // supabase-js's `on` is heavily overloaded; the discriminated-union
      // signature for `postgres_changes` doesn't always type-check cleanly
      // across versions. Cast keeps the call site type-safe enough — the
      // runtime contract (event/schema/table/filter) is enforced by the
      // Realtime infrastructure itself.
      "postgres_changes" as unknown as never,
      {
        event: "*",
        schema: "cogo",
        table: "application_ontology",
        filter: `tenant_id=eq.${opts.tenantId}`,
      } as never,
      ((payload: PostgresChangesPayloadShape) => {
        const row = (payload.new ?? payload.old) as
          | Record<string, unknown>
          | undefined
        if (!row) return

        // D-D2 — secondary client-side filter on app_id
        if (row.app_id !== opts.appId) return

        const eventTypeRaw = String(payload.eventType ?? payload.event ?? "")
        const changeKind = eventTypeRaw.toUpperCase() as
          | "INSERT"
          | "UPDATE"
          | "DELETE"

        const out: OntologyChangePayload = {
          changeKind,
          subject: String(row.subject ?? ""),
          tenantId: String(row.tenant_id ?? opts.tenantId),
          appId: String(row.app_id ?? opts.appId),
          branch: String(row.branch ?? "main"),
          versionSeq:
            typeof row.version_seq === "number"
              ? row.version_seq
              : undefined,
          receivedAt: Date.now(),
        }

        // T-439-04-03 — onChange callback throw must NOT crash the channel
        try {
          opts.onChange(out)
        } catch (cbErr) {
          // eslint-disable-next-line no-console
          console.error(
            "[realtime-subscriber] onChange callback error",
            cbErr,
          )
          // Do NOT rethrow — channel must stay healthy
        }

        // D-D3 — bridge to BroadcastChannel('cogo-kg-invalidate') listener
        if (!opts.skipBroadcast) {
          try {
            if (typeof BroadcastChannel !== "undefined") {
              const bc = new BroadcastChannel("cogo-kg-invalidate")
              bc.postMessage({
                // Phase 0431 D-C6 fields (existing listener may inspect):
                newVersion: out.versionSeq?.toString() ?? "",
                oldVersion: "",
                lastMigration: "",
                // Phase 0439 D-D3 extension — backwards compatible:
                source: "realtime",
                tenantId: out.tenantId,
                appId: out.appId,
                branch: out.branch,
                changeKind: out.changeKind,
                subject: out.subject,
                receivedAt: out.receivedAt,
              })
              bc.close()
            }
          } catch (bcErr) {
            // Best-effort; do not crash the subscriber on BroadcastChannel
            // failure (Service Worker / SSR contexts where BC may be absent).
            // Mirror shard-cache.ts L143-L150 idiom.
            // eslint-disable-next-line no-console
            console.warn(
              "[realtime-subscriber] BroadcastChannel post failed",
              bcErr,
            )
          }
        }
      }) as never,
    )
    .subscribe((status: string) => {
      // eslint-disable-next-line no-console
      if (status === "SUBSCRIBED") {
        console.info("[realtime-subscriber] subscribed", {
          channelName,
          tenantId: opts.tenantId,
          appId: opts.appId,
        })
      } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
        console.error("[realtime-subscriber] channel issue", {
          status,
          channelName,
        })
      }
    })

  return () => {
    void opts.supabaseClient.removeChannel(channel)
  }
}

/**
 * Internal payload shape for postgres_changes events. Mirrors what
 * @supabase/supabase-js@2 emits at runtime; declared here narrowly so we
 * don't import the unstable `RealtimePostgresChangesPayload` generic.
 */
interface PostgresChangesPayloadShape {
  eventType?: string
  event?: string
  new?: Record<string, unknown> | null
  old?: Record<string, unknown> | null
  schema?: string
  table?: string
}
